import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { BoardingYear, BranchMaster } from '../../AllAPI/AdminAPI'
import axios from 'axios'
import { toast } from 'react-toastify'

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --gold: #ffc107;
    --gold-deep: #e6a800;
    --gold-pale: #fffbec;
    --dark: #1a1200;
    --dark-2: #2b1f00;
    --dark-3: #3d2e00;
    --text-muted: #7a6000;
    --white: #ffffff;
    --shadow: 0 8px 40px rgba(255,193,7,0.18);
    --radius: 18px;
  }

  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--gold-pale);
    min-height: 100vh;
  }

  /* ─── PAGE ─── */
  .alp-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--gold-pale);
    position: relative;
    overflow: hidden;
  }
  .alp-page::before {
    content: '';
    position: fixed;
    top: -120px; right: -120px;
    width: 480px; height: 480px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,193,7,0.15) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
  .alp-page::after {
    content: '';
    position: fixed;
    bottom: -80px; left: -80px;
    width: 380px; height: 380px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,193,7,0.10) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  /* ─── HEADER ─── */
  .alp-header {
    position: relative;
    z-index: 10;
    background: linear-gradient(115deg, #1a1200 0%, #2b1f00 60%, #3d2e00 100%);
    padding: 16px 32px;
    display: flex;
    align-items: center;
    gap: 18px;
    box-shadow: 0 4px 28px rgba(255,193,7,0.22);
    border-bottom: 2.5px solid var(--gold);
  }
  .alp-logo-ring {
    width: 68px; height: 68px;
    border-radius: 50%;
    border: 2.5px solid var(--gold);
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(145deg, #2b1f00, #3d2e00);
    box-shadow: 0 0 0 5px rgba(255,193,7,0.12), 0 4px 18px rgba(0,0,0,0.5);
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
  }
  .alp-logo-ring::after {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,193,7,0.22), transparent);
    animation: shimmer 3s infinite;
  }
  @keyframes shimmer {
    0%   { left: -100%; }
    60%, 100% { left: 160%; }
  }
  .alp-logo-icon { font-size: 30px; }
  .alp-header-text h1 {
    font-family: 'Playfair Display', serif;
    font-size: 1.55rem;
    font-weight: 900;
    color: var(--gold);
    letter-spacing: 0.4px;
    line-height: 1.15;
  }
  .alp-header-text p {
    font-size: 0.78rem;
    color: rgba(255,193,7,0.55);
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 3px;
    font-weight: 500;
  }
  .alp-header-badge {
    margin-left: auto;
    background: rgba(255,193,7,0.12);
    border: 1px solid rgba(255,193,7,0.28);
    color: var(--gold);
    font-size: 0.7rem;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 5px 14px;
    border-radius: 50px;
    font-weight: 600;
    white-space: nowrap;
  }

  /* ─── ROLE BADGE on card ─── */
  .alp-role-badge {
    display: inline-block;
    background: rgba(255,193,7,0.13);
    border: 1px solid rgba(255,193,7,0.35);
    color: var(--gold-deep);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 4px 12px;
    border-radius: 50px;
    margin-bottom: 10px;
  }

  /* ─── MAIN ─── */
  .alp-main {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 44px 20px;
    position: relative;
    z-index: 2;
  }

  /* ─── CARD ─── */
  .alp-card {
    background: var(--white);
    border-radius: var(--radius);
    box-shadow: var(--shadow), 0 2px 0 var(--gold);
    padding: 42px 38px 34px;
    width: 100%;
    max-width: 430px;
    border: 1.5px solid rgba(255,193,7,0.18);
    position: relative;
    animation: cardIn 0.55s cubic-bezier(.22,1,.36,1) both;
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(28px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .alp-card-accent {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    border-radius: var(--radius) var(--radius) 0 0;
    background: linear-gradient(90deg, var(--gold-deep), var(--gold), #ffe066, var(--gold));
    background-size: 200%;
    animation: gradMove 3s linear infinite;
  }
  @keyframes gradMove {
    0%   { background-position: 0%; }
    100% { background-position: 200%; }
  }

  .alp-card-header { text-align: center; margin-bottom: 28px; }
  .alp-card-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--dark);
    margin-bottom: 4px;
  }
  .alp-card-sub {
    font-size: 0.82rem;
    color: var(--text-muted);
    letter-spacing: 0.4px;
  }

  /* ─── FORM ─── */
  .alp-form-group { margin-bottom: 18px; }
  .alp-label {
    display: block;
    font-size: 0.76rem;
    font-weight: 600;
    color: var(--dark);
    letter-spacing: 0.8px;
    text-transform: uppercase;
    margin-bottom: 6px;
  }
  .alp-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .alp-input-icon {
    position: absolute;
    left: 13px;
    font-size: 0.95rem;
    pointer-events: none;
    color: var(--text-muted);
  }
  .alp-select {
    width: 100%;
    padding: 11px 13px 11px 38px;
    border: 1.5px solid #e9d88a;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.93rem;
    color: var(--dark);
    background: var(--gold-pale);
    outline: none;
    appearance: none;
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  }
  .alp-select:focus {
    border-color: var(--gold);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(255,193,7,0.13);
  }
  .alp-select-arrow {
    position: absolute;
    right: 13px;
    font-size: 0.75rem;
    color: var(--text-muted);
    pointer-events: none;
  }

  /* info banner */
  .alp-info-banner {
    background: rgba(255,193,7,0.09);
    border: 1px solid rgba(255,193,7,0.28);
    border-radius: 9px;
    padding: 10px 14px;
    font-size: 0.80rem;
    color: var(--text-muted);
    margin-bottom: 20px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }
  .alp-info-icon { font-size: 1rem; flex-shrink: 0; margin-top: 1px; }

  /* ─── BUTTONS ─── */
  .alp-btn-row {
    display: grid;
    grid-template-columns: 1fr 1.6fr;
    gap: 10px;
    margin-top: 6px;
  }
  .alp-btn-back {
    padding: 13px;
    background: transparent;
    border: 1.5px solid #e9d88a;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.93rem;
    font-weight: 600;
    color: var(--text-muted);
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .alp-btn-back:hover {
    border-color: var(--gold);
    color: var(--dark);
    background: var(--gold-pale);
  }
  .alp-btn-proceed {
    padding: 13px;
    background: linear-gradient(135deg, var(--gold-deep) 0%, var(--gold) 60%, #ffe066 100%);
    border: none;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.97rem;
    font-weight: 700;
    color: var(--dark);
    cursor: pointer;
    letter-spacing: 0.4px;
    box-shadow: 0 4px 18px rgba(255,193,7,0.32);
    transition: transform 0.15s, box-shadow 0.15s, filter 0.15s;
    position: relative;
    overflow: hidden;
  }
  .alp-btn-proceed::after {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent);
    transition: left 0.4s;
  }
  .alp-btn-proceed:hover::after { left: 160%; }
  .alp-btn-proceed:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 26px rgba(255,193,7,0.42);
    filter: brightness(1.04);
  }
  .alp-btn-proceed:active { transform: translateY(0); }
  .alp-btn-proceed:disabled { opacity: 0.72; cursor: not-allowed; transform: none; }

  /* divider */
  .alp-divider {
    display: flex; align-items: center; gap: 10px; margin-top: 20px;
  }
  .alp-divider-line { flex: 1; height: 1px; background: #f0dfa0; }
  .alp-divider-text { font-size: 0.73rem; color: #b8a040; font-weight: 500; letter-spacing: 1px; }

  /* ─── FOOTER ─── */
  .alp-footer {
    position: relative;
    z-index: 10;
    background: linear-gradient(115deg, #141428, #0e1e1a);
    color: rgba(255,255,255,0.7);
    padding: 34px 40px 18px;
    border-top: 2.5px solid var(--gold);
  }
  .alp-footer-grid {
    display: grid;
    grid-template-columns: 1.5fr 1fr 1.5fr;
    gap: 30px;
    max-width: 960px;
    margin: 0 auto 24px;
  }
  .alp-footer-col h4 {
    font-family: 'Playfair Display', serif;
    font-size: 0.92rem;
    color: var(--gold);
    margin-bottom: 11px;
    letter-spacing: 0.4px;
  }
  .alp-footer-col p,
  .alp-footer-col a,
  .alp-footer-col span {
    font-size: 0.80rem;
    line-height: 1.75;
    color: rgba(255,255,255,0.58);
    text-decoration: none;
    display: block;
    transition: color 0.15s;
    cursor: pointer;
  }
  .alp-footer-col a:hover,
  .alp-footer-col span:hover { color: var(--gold); }

  .alp-portal-link {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: rgba(255,193,7,0.09);
    border: 1px solid rgba(255,193,7,0.24);
    color: var(--gold) !important;
    border-radius: 7px;
    padding: 7px 14px;
    font-size: 0.78rem !important;
    font-weight: 600;
    letter-spacing: 0.4px;
    margin-bottom: 8px;
    transition: background 0.2s, border-color 0.2s !important;
    cursor: pointer;
  }
  .alp-portal-link:hover {
    background: rgba(255,193,7,0.18) !important;
    border-color: rgba(255,193,7,0.5) !important;
    color: #fff !important;
  }



  .alp-footer-bottom {
    border-top: 1px solid rgba(255,193,7,0.14);
    padding-top: 14px;
    text-align: center;
    font-size: 0.74rem;
    color: rgba(255,255,255,0.28);
    letter-spacing: 0.4px;
  }
  .alp-footer-bottom span { color: var(--gold); }

  /* ─── RESPONSIVE ─── */
  @media (max-width: 640px) {
    .alp-header { padding: 14px 16px; gap: 12px; }
    .alp-header-text h1 { font-size: 1.1rem; }
    .alp-header-badge { display: none; }
    .alp-card { padding: 30px 20px 24px; }
    .alp-footer { padding: 26px 16px 14px; }
    .alp-footer-grid { grid-template-columns: 1fr; gap: 18px; }
  }
`

export default function AdminLoginPage2({ onLogin }) {
  const [campuses, setCampuses] = useState([])
  const [selectedCampus, setSelectedCampus] = useState('')
  const [loading, setLoading] = useState(false)
  const [years, setYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('')

  const navigate = useNavigate()
  const storedBranchId = sessionStorage.getItem('branch_id')
  const role = sessionStorage.getItem('role')
  const uname=sessionStorage.getItem('username')
  // console.log(uname)

  // ── Fetch campus + year on mount ──
  useEffect(() => {
    const fetchCampuses = async () => {
      try {
        const res = await axios.get(BranchMaster)
        const currentRole = sessionStorage.getItem('role')
        let filtered = []

        if (currentRole === 'admin' || currentRole === 'warden' || currentRole === 'office') {
          filtered = res.data.filter((b) => b.branch_id == storedBranchId)
          if (filtered.length > 0) setSelectedCampus(filtered[0].branch_id)
        } else if (currentRole === 'superintendent') {
          filtered = res.data
          if (filtered.length > 0) setSelectedCampus(filtered[0].branch_id)
        }

        setCampuses(filtered)
      } catch (error) {
        console.error('Error fetching campuses:', error)
      }
    }

    const fetchYears = async () => {
      try {
        const res = await axios.get(BoardingYear)
        setYears(res.data)
        if (res.data.length > 0) setSelectedYear(res.data[0].id)
      } catch (error) {
        console.error('Error fetching years:', error)
      }
    }

    fetchCampuses()
    fetchYears()
  }, [])

  // ── Submit ──
  const handleSubmit = (e) => {
    e.preventDefault()
    if (!selectedYear) {
      toast.error('Please select a year')
      return
    }
    const selectedYearObject = years.find((y) => y.id == selectedYear)
    sessionStorage.setItem('selected_branch', selectedCampus)
    sessionStorage.setItem('BoardingYear', selectedYearObject.boarding_year)
    toast.success('Login completed!')
    const currentRole = sessionStorage.getItem('role')
    sessionStorage.setItem('role', currentRole)


    if(currentRole === 'office'){
      navigate('/office/dashboard', { replace: true })
    } else{
      navigate(`/${currentRole}/dashboard`, { replace: true })
    }
    // navigate(`/${currentRole}/dashboard`, { replace: true })
  }

  // ── Back ──
  const backToLogin = () => {
    sessionStorage.clear()
    navigate('/admin/login')
  }

  // Role display label
  const roleLabel =
    role === 'admin'
      ? '⚙️ Administrator'
      : role === 'warden'
      ? '🛡️ Warden'
      : role === 'superintendent'
      ? '🏫 Superintendent'
      : role === 'office'
      ? '💼 office'
      : '👤 Staff'

  return (
    <>
      <style>{style}</style>
      <div className="alp-page">
        {/* ══════════ HEADER ══════════ */}
        <header className="alp-header">
          <div className="alp-logo-ring">
            <span className="alp-logo-icon">🏛️</span>
          </div>
          <div className="alp-header-text">
            <h1>Digambar Jain Boarding, Kolhapur</h1>
            <p>Staff Portal &amp; Administration</p>
          </div>
          <span className="alp-header-badge">Est. 1905</span>
        </header>

        {/* ══════════ MAIN ══════════ */}
        <main className="alp-main">
          <div className="alp-card">
            <div className="alp-card-accent" />

            <div className="alp-card-header">
              <div className="alp-role-badge">{roleLabel}</div>
              <h2 className="alp-card-title">Select Boarding Year</h2>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Campus */}
              {/* <div className="alp-form-group">
                <label className="alp-label">Select Campus</label>
                <div className="alp-input-wrap">
                  <span className="alp-input-icon">🏫</span>
                  <select
                    className="alp-select"
                    value={selectedCampus}
                    onChange={(e) => setSelectedCampus(e.target.value)}
                    required
                  >
                    {campuses.length === 0 && (
                      <option value="">Loading campuses…</option>
                    )}
                    {campuses.map((campus) => (
                      <option key={campus.branch_id} value={campus.branch_id}>
                        {campus.branch_name}
                      </option>
                    ))}
                  </select>
                  <span className="alp-select-arrow">▼</span>
                </div>
              </div> */}

              {/* Year */}
              <div className="alp-form-group">
                <label className="alp-label">Select Boarding Year</label>
                <div className="alp-input-wrap">
                  <span className="alp-input-icon">📅</span>
                  <select
                    className="alp-select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    required
                  >
                    {years.length === 0 && (
                      <option value="">Loading years…</option>
                    )}
                    {years.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.boarding_year}
                      </option>
                    ))}
                  </select>
                  <span className="alp-select-arrow">▼</span>
                </div>
              </div>

              {/* Buttons */}
              <div className="alp-btn-row">
                <button
                  type="button"
                  className="alp-btn-back"
                  onClick={backToLogin}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  className="alp-btn-proceed"
                  disabled={loading}
                >
                  {loading ? 'Proceeding…' : 'Proceed →'}
                </button>
              </div>
            </form>
          </div>
        </main>

        {/* ══════════ FOOTER ══════════ */}
        <footer className="alp-footer">
          <div className="alp-footer-grid">
            {/* About */}
            <div className="alp-footer-col">
              <h4>🏛️ Digambar Jain Boarding, Kolhapur</h4>
              <p>
                Providing safe, comfortable &amp; affordable accommodation to
                students since 1905. Your home away from home.
              </p>
            </div>

            {/* Staff Portals */}
            <div className="alp-footer-col">
              <h4>🔐 Staff Portals</h4>
              <a href="#/student/login" className="alp-portal-link">
                ⚙️ Student Login
              </a>
              <a href="#/admin/login" className="alp-portal-link">
                ⚙️ Admin Login
              </a>
              <a href="#/alumni/login" className="alp-portal-link">
                ⚙️ Alumni Login
              </a>
            </div>

            {/* Contact */}
            {/* 8412841008, 0231 2644339 */}
            {/* djbkkop@gmail.com */}
            <div className="alp-footer-col">
              <h4>📍 Contact Us</h4>
              <p>📞 +91 8412841008, 0231-2644339</p>
              <a href="mailto:djbkkop@gmail.com">✉️ djbkkop@gmail.com</a>
              <p style={{ marginTop: '6px' }}>
                🏠 Near, Dasara Chowk, New Shahupuri,
                <br />
                Kolhapur, Maharashtra 416002
              </p>
            </div>
          </div>

          <div className="alp-footer-bottom">
            © {new Date().getFullYear()}{' '}
            <span>Digambar Jain Boarding, Kolhapur</span>. All rights reserved.
            &nbsp;|&nbsp; Designed by <span>TechnoAarv Solution</span>.
          </div>
        </footer>
      </div>
    </>
  )
}
